import React, { useState, useEffect, useMemo } from 'react';
import { getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config/firebase';
import { vehiclesCol } from '../../firebase/firestore/collections';
import { Vehicle, VehicleStatus } from '../../types';
import { SEED_VEHICLES } from '../../constants/mockSeed';
import {
  Bus,
  Plus,
  Search,
  Filter,
  Wrench,
  CheckCircle2,
  Edit2,
  RefreshCw,
  Armchair,
  X,
  User,
  Phone,
  Trash2,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

export const AdminVehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVeh, setEditingVeh] = useState<Vehicle | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Form State
  const [plateNumber, setPlateNumber] = useState('');
  const [model, setModel] = useState('حافلة سياحية مجهزة 2024');
  const [capacity, setCapacity] = useState(50);
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [status, setStatus] = useState<VehicleStatus>('ACTIVE');
  const [notes, setNotes] = useState('مكيفة بالكامل — صيانة دورية معتمدة');

  // Load vehicles
  const loadVehicles = async () => {
    setIsLoading(true);
    try {
      const snap = await getDocs(vehiclesCol);
      if (!snap.empty) {
        setVehicles(snap.docs.map((d) => ({ ...d.data(), id: d.id })) as Vehicle[]);
      } else {
        setVehicles(SEED_VEHICLES);
      }
    } catch (err) {
      console.warn('Falling back to seed vehicles:', err);
      setVehicles(SEED_VEHICLES);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, []);

  // Filtered vehicles
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      const matchesSearch =
        searchTerm === '' ||
        v.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.driverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.driverPhone?.includes(searchTerm) ||
        v.notes?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'ALL' || v.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [vehicles, searchTerm, statusFilter]);

  // Reset pagination on filter change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, statusFilter]);

  const visibleVehicles = useMemo(() => {
    return filteredVehicles.slice(0, visibleCount);
  }, [filteredVehicles, visibleCount]);


  // Stats
  const stats = useMemo(() => {
    const total = vehicles.length;
    const active = vehicles.filter((v) => v.status === 'ACTIVE').length;
    const maintenance = vehicles.filter((v) => v.status === 'MAINTENANCE').length;
    const totalCapacity = vehicles.reduce((sum, v) => sum + (v.capacity || 50), 0);
    return { total, active, maintenance, totalCapacity };
  }, [vehicles]);

  // Open Create
  const handleOpenCreate = () => {
    setEditingVeh(null);
    setPlateNumber('');
    setModel('حافلة سياحية مجهزة 2024');
    setCapacity(50);
    setDriverName('');
    setDriverPhone('');
    setStatus('ACTIVE');
    setNotes('حافلة مكيفة بالكامل مع نظام أمان متطور');
    setIsModalOpen(true);
  };

  // Open Edit
  const handleOpenEdit = (v: Vehicle) => {
    setEditingVeh(v);
    setPlateNumber(v.vehicleNumber || v.plateNumber || '');
    setModel(v.model || 'حافلة سياحية مجهزة');
    setCapacity(v.capacity || 50);
    setDriverName(v.driverName || '');
    setDriverPhone(v.driverPhone || '');
    setStatus(v.status || 'ACTIVE');
    setNotes(v.notes || '');
    setIsModalOpen(true);
  };

  // Delete Vehicle cleanly
  const handleDeleteVehicle = async (v: Vehicle) => {
    if (!window.confirm(`هل أنت متأكد من حذف المركبة (${v.vehicleNumber || v.plateNumber}) نهائياً من قاعدة بيانات الأسطول؟`)) {
      return;
    }
    setActionLoading(true);
    try {
      const docRef = doc(db, 'vehicles', v.id);
      await deleteDoc(docRef);
      setVehicles((prev) => prev.filter((item) => item.id !== v.id));
      setMessage({ text: `تم حذف المركبة ${v.vehicleNumber} بنجاح من قاعدة البيانات.`, type: 'success' });
    } catch (err: any) {
      setVehicles((prev) => prev.filter((item) => item.id !== v.id));
      setMessage({ text: 'تم حذف المركبة محلياً.', type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Toggle Maintenance / Active
  const handleToggleMaintenance = async (v: Vehicle) => {
    const nextStatus: VehicleStatus = v.status === 'ACTIVE' ? 'MAINTENANCE' : 'ACTIVE';
    setActionLoading(true);

    try {
      const docRef = doc(db, 'vehicles', v.id);
      await updateDoc(docRef, { status: nextStatus, updatedAt: new Date().toISOString() });
      setVehicles((prev) => prev.map((item) => (item.id === v.id ? { ...item, status: nextStatus } : item)));
      setMessage({
        text: `تم نقل المركبة ${v.vehicleNumber} إلى ${nextStatus === 'ACTIVE' ? 'الخدمة النشطة' : 'وضع الصيانة'}.`,
        type: 'success',
      });
    } catch (err: any) {
      setVehicles((prev) => prev.map((item) => (item.id === v.id ? { ...item, status: nextStatus } : item)));
      setMessage({ text: 'تم تحديث حالة المركبة محلياً.', type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Save Vehicle
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plateNumber.trim()) {
      alert('يرجى إدخال رقم لوحة السيارة');
      return;
    }

    const finalCapacity = Math.max(1, Number(capacity) || 50);

    setActionLoading(true);
    const nowIso = new Date().toISOString();

    try {
      if (editingVeh) {
        const docRef = doc(db, 'vehicles', editingVeh.id);
        const updated: Vehicle = {
          ...editingVeh,
          vehicleNumber: plateNumber.trim(),
          plateNumber: plateNumber.trim(),
          model: model.trim(),
          capacity: finalCapacity,
          driverName: driverName.trim(),
          driverPhone: driverPhone.trim(),
          status,
          notes: notes.trim(),
          updatedAt: nowIso,
        };
        await setDoc(docRef, updated, { merge: true });
        setVehicles((prev) => prev.map((item) => (item.id === editingVeh.id ? updated : item)));
        setMessage({ text: 'تم تحديث بيانات المركبة بنجاح.', type: 'success' });
      } else {
        const id = 'veh_' + Math.random().toString(36).substring(2, 7);
        const newVehicle: Vehicle = {
          id,
          vehicleNumber: plateNumber.trim(),
          plateNumber: plateNumber.trim(),
          type: 'BUS',
          model: model.trim(),
          capacity: finalCapacity,
          driverName: driverName.trim(),
          driverPhone: driverPhone.trim(),
          status,
          notes: notes.trim(),
          createdAt: nowIso,
          updatedAt: nowIso,
        };
        const docRef = doc(db, 'vehicles', id);
        await setDoc(docRef, newVehicle, { merge: true });
        setVehicles((prev) => [newVehicle, ...prev]);
        setMessage({ text: 'تمت إضافة المركبة لأسطول العمليات بنجاح.', type: 'success' });
      }
      setIsModalOpen(false);
    } catch (err: any) {
      if (editingVeh) {
        setVehicles((prev) =>
          prev.map((item) =>
            item.id === editingVeh.id
              ? {
                  ...item,
                  vehicleNumber: plateNumber,
                  plateNumber,
                  model,
                  capacity: finalCapacity,
                  driverName,
                  driverPhone,
                  status,
                  notes,
                }
              : item
          )
        );
      } else {
        const id = 'veh_' + Date.now();
        setVehicles((prev) => [
          {
            id,
            vehicleNumber: plateNumber,
            plateNumber,
            type: 'BUS',
            model,
            capacity: finalCapacity,
            driverName,
            driverPhone,
            status,
            notes,
            createdAt: nowIso,
            updatedAt: nowIso,
          },
          ...prev,
        ]);
      }
      setIsModalOpen(false);
      setMessage({ text: 'تم حفظ بيانات المركبة محلياً.', type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-header-title-box">
          <h2>
            <Bus size={24} className="text-[#E5232A]" />
            <span>أسطول الحافلات والمركبات المعتمدة</span>
          </h2>
          <p>متابعة أسطول حافلات وسيارات النقل الجامعي، إدارة السائقين، وتحديد السعة الاستيعابية لكل مركبة لجدولة الرحلات.</p>
        </div>

        <div className="admin-header-actions-row">
          <button type="button" className="btn-admin-primary" onClick={handleOpenCreate}>
            <Plus size={18} />
            <span>إضافة مركبة للأسطول</span>
          </button>
          <button type="button" className="btn-admin-outline" onClick={loadVehicles} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Message alert */}
      {message && (
        <div className={`p-3 rounded-xl border flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          <CheckCircle2 size={18} />
          <span>{message.text}</span>
        </div>
      )}

      {/* Stats */}
      <div className="admin-stats-strip">
        <div className="admin-stat-card">
          <div className="stat-icon blue">
            <Bus size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.total}</span>
            <span className="stat-lbl">إجمالي سيارات الأسطول</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon green">
            <CheckCircle2 size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.active}</span>
            <span className="stat-lbl">مركبات نشطة في الخدمة</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon amber">
            <Wrench size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.maintenance}</span>
            <span className="stat-lbl">مركبات في الصيانة الدورية</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon purple">
            <Armchair size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.totalCapacity}</span>
            <span className="stat-lbl">إجمالي سعة المقاعد المتاحة</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar-card">
        <div className="admin-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            className="admin-search-input"
            placeholder="البحث برقم اللوحة، الموديل، الملاحظات..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="admin-filter-group">
          <Filter size={16} className="text-slate-400" />
          <select
            className="admin-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">كافة الحالات</option>
            <option value="ACTIVE">نشطة في الخدمة (ACTIVE)</option>
            <option value="MAINTENANCE">في الصيانة (MAINTENANCE)</option>
            <option value="INACTIVE">خارج الخدمة (INACTIVE)</option>
          </select>
        </div>
      </div>

      {/* Vehicles Cards Grid */}
      {isLoading ? (
        <div className="table-empty-state bg-white rounded-2xl border p-12">
          <RefreshCw size={32} className="animate-spin text-[#E5232A]" />
          <p>جاري تحميل أسطول السيارات...</p>
        </div>
      ) : filteredVehicles.length === 0 ? (
        <div className="table-empty-state bg-white rounded-2xl border p-12">
          <Bus size={40} />
          <p>لا توجد مركبات تطابق شروط البحث الحالية.</p>
        </div>
      ) : (
        <>
          <div className="admin-cards-grid">
            {visibleVehicles.map((v) => (
              <div key={v.id} className="admin-item-card">
                <div className="admin-item-top">
                  <div className="item-icon-title">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-[#E5232A] flex items-center justify-center font-bold">
                      <Bus size={20} />
                    </div>
                    <div>
                      <strong className="text-base font-mono block text-slate-900">{v.vehicleNumber}</strong>
                      <span className="text-xs text-slate-500 block">{v.model || 'Toyota HiAce'}</span>
                    </div>
                  </div>

                  <span
                    className={`status-pill ${
                      v.status === 'ACTIVE'
                        ? 'active'
                        : v.status === 'MAINTENANCE'
                        ? 'pending'
                        : 'cancelled'
                    }`}
                  >
                    {v.status === 'ACTIVE'
                      ? 'نشطة'
                      : v.status === 'MAINTENANCE'
                      ? 'صيانة'
                      : 'معطلة'}
                  </span>
                </div>

                <div className="admin-item-meta">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">سعة الركاب:</span>
                    <strong className="text-slate-900 font-mono bg-rose-50 text-[#E5232A] px-2 py-0.5 rounded-lg border border-rose-100">
                      {v.capacity || 50} مقعداً
                    </strong>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">السائق المسؤول:</span>
                    <span className="font-bold text-slate-800 flex items-center gap-1">
                      <User size={13} className="text-blue-600" />
                      <span>{v.driverName ? v.driverName : 'لم يُحدد'}</span>
                    </span>
                  </div>

                  {v.driverPhone && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">هاتف السائق:</span>
                      <a
                        href={`tel:${v.driverPhone}`}
                        className="font-mono text-emerald-700 font-bold hover:underline flex items-center gap-1"
                        dir="ltr"
                      >
                        <Phone size={12} />
                        <span>{v.driverPhone}</span>
                      </a>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">حالة التكييف:</span>
                    <span className="text-emerald-700 font-bold">مكيفة بالكامل ✓</span>
                  </div>

                  {v.notes && (
                    <div className="text-xs text-slate-500 mt-1 italic">
                      ملاحظات: {v.notes}
                    </div>
                  )}
                </div>

                <div className="admin-item-actions">
                  <button
                    type="button"
                    className="btn-admin-outline"
                    onClick={() => handleOpenEdit(v)}
                  >
                    <Edit2 size={14} />
                    <span>تعديل</span>
                  </button>

                  <button
                    type="button"
                    className={v.status === 'ACTIVE' ? 'btn-admin-danger' : 'btn-admin-success'}
                    onClick={() => handleToggleMaintenance(v)}
                    disabled={actionLoading}
                  >
                    <Wrench size={14} />
                    <span>{v.status === 'ACTIVE' ? 'تحويل للصيانة' : 'إعادة للخدمة'}</span>
                  </button>

                  <button
                    type="button"
                    className="btn-admin-outline text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleDeleteVehicle(v)}
                    disabled={actionLoading}
                    title="حذف المركبة من الأسطول"
                  >
                    <Trash2 size={14} />
                    <span>حذف</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredVehicles.length > 0 && (
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredVehicles.length)}
              totalCount={filteredVehicles.length}
              itemName="مركبة"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredVehicles.length)}
            />
          )}
        </>
      )}

      {/* Add / Edit Vehicle Modal */}
      {isModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSave}>
              <div className="admin-modal-header">
                <h3>{editingVeh ? `تعديل مركبة: ${editingVeh.vehicleNumber}` : 'إضافة مركبة جديدة للأسطول'}</h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="form-grid-2">
                  <div className="admin-form-group">
                    <label>رقم اللوحة المعدنية *</label>
                    <input
                      type="text"
                      required
                      placeholder="مثال: س ق د 1842"
                      value={plateNumber}
                      onChange={(e) => setPlateNumber(e.target.value)}
                    />
                  </div>

                  <div className="admin-form-group">
                    <label>الموديل وسنة الصنع *</label>
                    <input
                      type="text"
                      required
                      placeholder="مثال: Toyota HiAce 2024 أو حافلة مرسيدس"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    />
                  </div>
                </div>

                {/* Driver Name & Driver Phone */}
                <div className="form-grid-2">
                  <div className="admin-form-group">
                    <label>اسم السائق المسؤول</label>
                    <input
                      type="text"
                      placeholder="مثال: أحمد عبد الرحمن"
                      value={driverName}
                      onChange={(e) => setDriverName(e.target.value)}
                    />
                  </div>

                  <div className="admin-form-group">
                    <label>رقم هاتف السائق</label>
                    <input
                      type="tel"
                      placeholder="مثال: 01012345678"
                      value={driverPhone}
                      onChange={(e) => setDriverPhone(e.target.value)}
                      dir="ltr"
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="admin-form-group">
                    <label>سعة مقاعد المركبة (عدد المقاعد المتاحة للحجز) *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="120"
                      value={capacity}
                      onChange={(e) => setCapacity(Number(e.target.value))}
                      className="font-bold"
                    />
                    <span className="text-xs text-slate-500">
                      حدد السعة الاستيعابية الفعلية (مثلاً: 14، 28، 33، 50 راكباً). سيتم اعتماد هذه السعة عند جدولة الرحلات بها.
                    </span>
                  </div>

                  <div className="admin-form-group">
                    <label>الحالة التشغيلية *</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as VehicleStatus)}
                    >
                      <option value="ACTIVE">نشطة في الخدمة (ACTIVE)</option>
                      <option value="MAINTENANCE">تحت الصيانة الدورية (MAINTENANCE)</option>
                      <option value="INACTIVE">خارج الخدمة (INACTIVE)</option>
                    </select>
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>ملاحظات المركبة وفحص الأمان</label>
                  <textarea
                    rows={3}
                    placeholder="ملاحظات الصيانة الدورية، حالة التكييف، فحص الإطارات..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  disabled={actionLoading}
                >
                  <CheckCircle2 size={16} />
                  <span>{editingVeh ? 'حفظ التعديلات' : 'إضافة للأسطول'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
